/**
 * Chiaro o scuro, su **questo dispositivo**.
 *
 * È la terza preferenza di dispositivo della console, accanto al suono e alle
 * scorciatoie, e sta lì per la stessa ragione: riguarda com'è fatto questo
 * posto — uno schermo al buio, una sede con il neon — e non chi è entrato.
 *
 * Il predefinito è **chiaro**, e non è il sistema operativo a deciderlo. La
 * console seguiva `prefers-color-scheme`: chi aveva il telefono in scuro se la
 * ritrovava scura senza averlo chiesto, e senza un modo per tornare indietro
 * (`docs/DECISIONI.md`, AQ1). Lo scuro adesso è una scelta, e si disfa dallo
 * stesso posto in cui si fa.
 *
 * L'archiviazione può essere negata (finestra privata, impostazioni del
 * browser): in quel caso la scelta vale per questa scheda, e non è un errore
 * da mostrare a nessuno — la stessa regola di `preferences.ts`.
 */

export const THEME_MODES = ['light', 'dark'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];

const STORAGE_KEY = 'console.theme';

/**
 * Il predefinito, scritto una volta sola.
 *
 * Chiaro: è la palette su cui il contrasto di ogni schermata è stato
 * progettato per prima, ed è quella che una persona vede se non chiede niente.
 */
export const DEFAULT_THEME: ThemeMode = 'light';

export function isThemeMode(value: string | null | undefined): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

/**
 * La palette **applicata adesso**, letta dall'attributo che il foglio di stile
 * guarda.
 *
 * Non è un doppione di `readThemeMode`: quella legge la scelta salvata, questa
 * legge cosa si sta vedendo. Nell'applicazione vera coincidono, perché è
 * `setupTheme()` senza argomenti a scriverlo; nella galleria no — lì la
 * palette arriva da `?theme=` e non tocca l'archivio. Il selettore deve dire
 * cosa si vede, non cosa è stato salvato: un controllo che dichiara "Chiaro"
 * su uno schermo scuro è uno stato dichiarato falso.
 */
export function currentThemeMode(): ThemeMode {
  const applied = globalThis.document?.documentElement?.dataset?.theme;
  return isThemeMode(applied) ? applied : readThemeMode();
}

/** La scelta di questo dispositivo, o il chiaro se non ce n'è una. */
export function readThemeMode(): ThemeMode {
  try {
    const stored = globalThis.localStorage?.getItem(STORAGE_KEY);
    return isThemeMode(stored) ? stored : DEFAULT_THEME;
  } catch {
    return DEFAULT_THEME;
  }
}

/**
 * Scrive l'attributo che il foglio di stile guarda.
 *
 * Sempre esplicito, anche per il chiaro: un attributo assente e un attributo
 * `light` devono essere la stessa cosa a schermo, ma solo uno dei due si legge
 * da un test o da una console del browser.
 */
export function applyThemeMode(mode: ThemeMode): void {
  const root = globalThis.document?.documentElement;
  if (!root) return;
  root.dataset.theme = mode;
}

export function setThemeMode(mode: ThemeMode): void {
  applyThemeMode(mode);
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, mode);
  } catch {
    // Vale per questa scheda.
  }
}

/** Legge la scelta e la applica: è ciò che si fa prima del primo disegno. */
export function setupTheme(mode: ThemeMode = readThemeMode()): ThemeMode {
  applyThemeMode(mode);
  return mode;
}
